import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Lead {
  id: string
  name: string
  email: string
  score: number
  grade: string
  status: string
  engagement_score: number
  fit_score: number
  intent_score: number
  source: string
  stage: string
}

interface ScoringRule {
  id: string
  attribute: string
  condition: string
  points: number
  category: string
  active: boolean
}

interface ScoreDistribution {
  id: string
  range: string
  count: number
  percentage: number
  label: string
}

interface Statistics {
  total_leads: number
  avg_score: number
  mql_count: number
  sql_count: number
  conversion_rate: number
  avg_time_to_qualify: number
}

export default function LeadScoringPage() {
  const [leads, setLeads] = useState<Lead[]>([])
  const [rules, setRules] = useState<ScoringRule[]>([])
  const [distribution, setDistribution] = useState<ScoreDistribution[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [leadsRes, rulesRes, distRes, statsRes] = await Promise.all([
        fetch('/api/leadscoring/leads'),
        fetch('/api/leadscoring/rules'),
        fetch('/api/leadscoring/distribution'),
        fetch('/api/leadscoring/statistics')
      ])
      if (leadsRes.ok) setLeads(await leadsRes.json())
      if (rulesRes.ok) setRules(await rulesRes.json())
      if (distRes.ok) setDistribution(await distRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching lead scoring data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getStatusBadge = (status: string) => {
    if (status === 'hot') return 'bg-red-100 text-red-800'
    if (status === 'warm') return 'bg-yellow-100 text-yellow-800'
    return 'bg-blue-100 text-blue-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Lead Scoring</h1>
          <p className="mt-2 text-gray-600">Phase 242 - Lead qualification and scoring</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_leads}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Score</p>
              <p className={`text-2xl font-bold ${getScoreColor(statistics.avg_score)}`}>{statistics.avg_score}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">MQLs</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.mql_count}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">SQLs</p>
              <p className="text-2xl font-bold text-green-600">{statistics.sql_count}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Conversion</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.conversion_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Time</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.avg_time_to_qualify}d</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Scored Leads</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {leads.map((lead) => (
                  <tr key={lead.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{lead.name}</div>
                      <div className="text-sm text-gray-500">{lead.email}</div>
                    </td>
                    <td className={`px-6 py-4 font-bold ${getScoreColor(lead.score)}`}>{lead.score}</td>
                    <td className="px-6 py-4 font-medium text-gray-900">{lead.grade}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(lead.status)}`}>{lead.status}</span>
                    </td>
                    <td className="px-6 py-4 text-blue-600">{lead.engagement_score}</td>
                    <td className="px-6 py-4 text-green-600">{lead.fit_score}</td>
                    <td className="px-6 py-4 text-purple-600">{lead.intent_score}</td>
                    <td className="px-6 py-4 text-gray-500">{lead.stage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Scoring Rules</h2>
            </div>
            <div className="p-6 space-y-3">
              {rules.map((rule) => (
                <div key={rule.id} className={`p-3 rounded-lg ${rule.active ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{rule.attribute}</span>
                    <span className="text-lg font-bold text-green-600">+{rule.points}</span>
                  </div>
                  <div className="text-sm text-gray-500">{rule.condition}</div>
                  <span className="text-xs text-blue-600">{rule.category}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Score Distribution</h2>
            </div>
            <div className="p-6 space-y-4">
              {distribution.map((dist) => (
                <div key={dist.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{dist.label}</span>
                    <span className="text-lg font-bold text-blue-600">{dist.count}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{dist.range}</span>
                    <span>{dist.percentage}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full mt-2">
                    <div className="h-2 bg-blue-500 rounded-full" style={{width: `${dist.percentage}%`}}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
